"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Admin only",
    rule: "Settings → Organization → Profile. Non-admins do not see this card",
  },
  {
    topic: "Required to save",
    rule: "Company Name, Email, Phone, Street Address 1, City, State, Country, PIN, GSTIN (15-digit), PAN (10-character). Cancel discards and returns to All settings",
  },
  {
    topic: "Logo and signature",
    rule: "PNG or JPG, up to 5MB each. They print on document PDFs and report PDFs that use the organisation profile — not on My Profile",
  },
  {
    topic: "Base currency",
    rule: "Shown here as a locked code. The gear opens the same currency editor as Currencies. Tooltip: transactions and financial reports are shown in the base currency. Full currency list is Settings → Currencies",
  },
  {
    topic: "GSTIN vs GST portal",
    rule: "GSTIN on this form is the company's tax ID on documents. The NIC portal username/password for IRN and e-way is Settings → GST portal credentials",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. Basic Details" },
      { heading: "2. Tax Information" },
      { heading: "3. Address Details" },
      { heading: "4. Organization Preferences" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function ProfileSettingsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Profile
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — company identity on documents, reports, and GST
            fields
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Title <B>Company Profile</B>. This is the organisation, not the
            logged-in user. Save writes the company record used on PDFs, tax
            IDs, and how dates/currency display.
          </p>
          <Flow steps={["Settings", "Organization", "Profile", "edit", "Save"]} />
          <Callout type="note">
            My Profile is your user card. This screen is company name, logo,
            GSTIN, address, and preferences.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Basic Details
          </h3>
          <Definitions
            items={[
              {
                term: "Fields",
                desc: "Company Name, Email Address, Phone Number (all required). Company Logo and authorized signature — drag-and-drop or browse, PNG/JPG up to 5MB. Change Logo / Change Signature when a file is already there",
              },
              {
                term: "What it affects",
                desc: "Company name and contact on documents. Logo and signature on generated PDFs (invoices, quotes, and the same profile Reports export uses)",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Tax Information
          </h3>
          <Definitions
            items={[
              {
                term: "GSTIN",
                desc: "Required. 15-digit GST Identification Number — the company's GSTIN on documents and GST flows",
              },
              {
                term: "PAN Number",
                desc: "Required. 10-character PAN",
              },
              {
                term: "Import Export Code",
                desc: "Optional IEC",
              },
              {
                term: "What it affects",
                desc: "Tax IDs printed and stored on the company. It does not log you into the NIC portal — that is GST portal credentials",
              },
            ]}
          />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Address Details
          </h3>
          <Definitions
            items={[
              {
                term: "Fields",
                desc: "Street Address 1 (required), Street Address 2. Country → State → City (cascading, required). PIN (required). Fax Number (optional)",
              },
              {
                term: "What it affects",
                desc: "Company address on PDFs. Country and city also set the read-only Time Zone under Organization Preferences",
              },
            ]}
          />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Organization Preferences
          </h3>
          <Definitions
            items={[
              {
                term: "Base Currency",
                desc: "Read-only code. Gear → edit the base currency (same modal as Currencies). Cannot be deleted from the currency list",
              },
              {
                term: "Fiscal Year",
                desc: "Month-to-month window. April–March is the default option. Saved on the company",
              },
              {
                term: "Organization Language",
                desc: "Picker (English is the default option). Stored on the company; Books screens stay in English",
              },
              {
                term: "Time Zone",
                desc: "Read-only. Tooltip: Value comes from your company profile (country and city). It is read-only here",
              },
              {
                term: "Date Format",
                desc: "Picker (DD/MM/YYYY is the default option). Saved on the company",
              },
            ]}
          />
          <Steps
            items={[
              <>
                Edit the sections you need. Required fields must be filled.
              </>,
              <>
                Click <B>Save</B> at the bottom. <B>Cancel</B> drops changes and
                goes back to All settings.
              </>,
            ]}
          />
          <Screenshot caption="Company Profile — Basic Details, Tax Information, Address, Organization Preferences, Save" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/currencies"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Currencies <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Full list and exchange rates — base is also on this Profile
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/gst-portal-credentials"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              GST portal credentials <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              NIC login for IRN / e-way — not the GSTIN field here
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/my-profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              My Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Your user card — not this company form
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
